import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, message, Divider, Spin, Breadcrumb, Layout, theme, Typography } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalculatorOutlined, FilterOutlined } from "@ant-design/icons";
import {
    useCalculateBrandEquityMutation,
    useGetCohortsByBrandQuery,
    useGetBrandsQuery,
    useGetProfileMutation,
    useGetAIQuotaStatusQuery,
    useGetSurveysQuery,
} from "../services/nestjs-base-api";
import {
    ICalculateBrandEquityRequest,
    PeriodEnum,
} from "../services/nestjs-base-api/types";
import { useSubscriptionFeatures, SubscriptionFeature } from "../hooks/useSubscriptionFeatures";
import BrandEquityFilters from "../components/BrandEquity/BrandEquityFilters";
import SubscriptionUpgradeModal from "../components/SubscriptionUpgradeModal";
import { AIQuotaWidget } from "../components/AIQuotaWidget";

const { Content } = Layout;
const { Title, Text } = Typography;

const DataSelectionPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo'); // Track where to navigate after calculation

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [filters, setFilters] = useState<ICalculateBrandEquityRequest & {
        brandId?: number;
        periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        countryCodes?: string[];
    }>({});
    const [calculating, setCalculating] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const [calculateBrandEquity] = useCalculateBrandEquityMutation();
    const [getProfile] = useGetProfileMutation();

    const { data: quotaStatus } = useGetAIQuotaStatusQuery({
        jwtToken: user?.jwtToken || '',
    }, {
        skip: !user?.jwtToken
    });

    // Subscription validation
    const subscriptionFeatures = useSubscriptionFeatures(filters.brandId);
    const brandEquityUpgradeInfo = subscriptionFeatures.requiresUpgrade(SubscriptionFeature.BrandEquity);

    // Load user profile (includes organization data)
    const [profileData, setProfileData] = useState<any>(null);
    const [loadingProfile, setLoadingProfile] = useState(false);

    // Local storage key for filters
    const FILTERS_STORAGE_KEY = `brandEquityFilters_${user?.id || 'default'}`;

    useEffect(() => {
        const loadProfile = async () => {
            if (!user?.jwtToken) return;

            setLoadingProfile(true);
            try {
                const result = await getProfile({ jwtToken: user.jwtToken }).unwrap();
                setProfileData(result);
            } catch (error) {
                console.error('Failed to load profile:', error);
            } finally {
                setLoadingProfile(false);
            }
        };

        loadProfile();
    }, [user?.jwtToken, getProfile]);

    // Load brands for the organization
    const { data: brandsData } = useGetBrandsQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: profileData?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !profileData?.organisation?.id }
    );

    // Load surveys for country selection
    const { data: surveysData } = useGetSurveysQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: profileData?.organisation?.id || 0
        },
        { skip: !user?.jwtToken || !profileData?.organisation?.id }
    );

    // Load filters from localStorage and set defaults
    useEffect(() => {
        if (!brandsData || brandsData.length === 0) return;

        // Skip if filters are already set
        if (filters.brandId) return;

        // Try to load from localStorage
        const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
        let loadedFilters: (ICalculateBrandEquityRequest & {
            brandId?: number;
            periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        }) | null = null;

        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                // Validate that the saved brand still exists
                if (parsed && parsed.brandId && brandsData.some(b => b.id === parsed.brandId)) {
                    // Migrate old filters: remove obsolete fields
                    if (parsed.surveyIds) {
                        delete parsed.surveyIds;
                        parsed.cohortIds = undefined; // Migrate to "All Cohorts"
                    }
                    delete parsed.filterMode; // Remove obsolete field

                    // For preset periods, don't calculate dates - let the backend handle it
                    if (parsed.periodType && parsed.periodType !== 'custom') {
                        loadedFilters = {
                            ...parsed,
                            // Don't set dateFrom/dateTo for predefined periods
                            dateFrom: undefined,
                            dateTo: undefined,
                        };
                    } else {
                        loadedFilters = parsed;
                    }
                }
            } catch (e) {
                console.error('Failed to parse saved filters', e);
            }
        }

        if (loadedFilters) {
            // Use saved filters
            setFilters(loadedFilters);
        } else if (brandsData.length > 0) {
            // Default to first brand with last 30 days
            setFilters({
                brandId: brandsData[0].id,
                periodType: 'last30',
                period: PeriodEnum.THIRTY_DAYS,
                // Don't set dates for predefined period
            });
        }
    }, [brandsData, filters.brandId, FILTERS_STORAGE_KEY]);

    // Save filters to localStorage when they change
    useEffect(() => {
        if (filters.brandId) {
            // For preset periods, don't save calculated dates - only save the period type
            const filtersToSave = { ...filters };
            if (filters.periodType && filters.periodType !== 'custom') {
                // Remove calculated dates for preset periods - they'll be recalculated fresh
                delete filtersToSave.dateFrom;
                delete filtersToSave.dateTo;
            }
            localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave));
        }
    }, [filters, FILTERS_STORAGE_KEY]);

    const handleCalculate = useCallback(async () => {
        if (!user?.jwtToken) {
            message.error("Authentication required");
            return;
        }

        if (!filters.brandId) {
            message.error("Please select a brand first");
            return;
        }

        if (!filters.period && (!filters.dateFrom || !filters.dateTo)) {
            message.error("Period or date range must be specified");
            return;
        }

        // Prepare filters based on period type
        let updatedFilters = { ...filters };
        if (filters.periodType && filters.periodType !== 'custom') {
            // Map frontend period types to API PeriodEnum
            let period: PeriodEnum;
            switch (filters.periodType) {
                case 'last30':
                    period = PeriodEnum.THIRTY_DAYS;
                    break;
                case 'last60':
                    period = PeriodEnum.SIXTY_DAYS;
                    break;
                case 'last90':
                    period = PeriodEnum.NINETY_DAYS;
                    break;
                case 'last120':
                    period = PeriodEnum.ONE_TWENTY_DAYS;
                    break;
                case 'last180':
                    period = PeriodEnum.ONE_EIGHTY_DAYS;
                    break;
                case 'last365':
                    period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                    break;
                case 'allTime':
                    period = PeriodEnum.ALL_TIME;
                    break;
                default:
                    period = PeriodEnum.THIRTY_DAYS;
            }

            updatedFilters = {
                ...updatedFilters,
                period,
                // Clear date fields for predefined periods
                dateFrom: undefined,
                dateTo: undefined,
            };
        } else if (filters.periodType === 'custom') {
            // For custom date range, use DATE_RANGE period with dates
            updatedFilters = {
                ...updatedFilters,
                period: PeriodEnum.DATE_RANGE,
                // Keep dateFrom and dateTo as they are
            };
        }

        const cacheKey = `brandEquityResults_${user.id}`;

        setCalculating(true);
        try {
            // Extract only the fields needed for the API call and convert brandId to brandIds array
            const { brandId, periodType, ...apiPayload } = updatedFilters;
            const apiPayloadWithBrandIds = {
                ...apiPayload,
                brandIds: brandId ? [brandId] : undefined
            };
            const result = await calculateBrandEquity({
                jwtToken: user.jwtToken,
                organisationId: profileData.organisation.id,
                payload: apiPayloadWithBrandIds,
            }).unwrap();

            // Cache the results
            localStorage.setItem(cacheKey, JSON.stringify({
                data: result,
                timestamp: Date.now()
            }));

            message.success('Brand equity calculation completed successfully');

            // Navigate based on returnTo parameter or default to visualization
            setTimeout(() => {
                if (returnTo === 'dashboard') {
                    navigate('/dashboard');
                } else if (returnTo === 'statements') {
                    navigate('/dashboard/analytics/statements');
                } else {
                    navigate('/dashboard/analytics/visualization');
                }
            }, 500);
        } catch (error) {
            console.error("Calculation failed:", error);

            // Handle specific error types
            if (error && typeof error === 'object' && 'status' in error) {
                if (error.status === 403) {
                    // Check if it's a quota exceeded error
                    const errorData = 'data' in error ? error.data : null;
                    const errorCode = errorData && typeof errorData === 'object' && 'error' in errorData
                        ? (errorData as any).error
                        : '';
                    const errorMessage = errorData && typeof errorData === 'object' && 'message' in errorData
                        ? (errorData as any).message
                        : '';

                    if (errorCode === 'AI_QUOTA_EXCEEDED') {
                        message.error({
                            content: 'AI quota exceeded. Your organization has reached its AI token limit. Please contact your administrator to upgrade your plan.',
                            duration: 8,
                        });
                    } else if (errorMessage && (errorMessage.includes('subscription') || errorMessage.includes('Professional'))) {
                        setShowUpgradeModal(true);
                    } else {
                        message.error('Access denied. You do not have permission to perform this action.');
                    }
                } else if (error.status === 400) {
                    // Check for specific error message about no responses found
                    const errorData = 'data' in error ? error.data : null;
                    const errorMessage = errorData && typeof errorData === 'object' && 'message' in errorData
                        ? (errorData as any).message
                        : '';

                    if (errorMessage && errorMessage.includes('No question responses found')) {
                        message.error('No question responses found for the selected filters. Please adjust your selection.');
                    } else {
                        message.error('Invalid request. Please check your filters and try again.');
                    }
                } else {
                    message.error('An error occurred while calculating brand equity. Please try again.');
                }
            } else {
                message.error('Calculation failed. Please try again.');
            }
        } finally {
            setCalculating(false);
        }
    }, [user, filters, profileData, calculateBrandEquity, returnTo, navigate]);

    // Load cohorts for the selected brand
    const { data: cohortsData } = useGetCohortsByBrandQuery(
        {
            jwtToken: user?.jwtToken || "",
            organisationId: profileData?.organisation?.id || 0,
            brandId: filters.brandId || 0
        },
        { skip: !user?.jwtToken || !profileData?.organisation?.id || !filters.brandId }
    );

    const handleBrandChange = (brandId: number | undefined) => {
        // Clear cohort and country selections when brand changes
        setFilters(prev => ({
            ...prev,
            brandId,
            cohortIds: undefined,
            countryCodes: undefined,
        }));
    };

    const handleFiltersChange = (newFilters: ICalculateBrandEquityRequest & {
        brandId?: number;
        periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        countryCodes?: string[];
    }) => {
        setFilters(newFilters);
    };

    // Show loading state while profile is loading or if no organization
    if (loadingProfile || !profileData?.organisation) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <Spin size="large" />
                        <div style={{ fontSize: '16px', marginTop: '16px', marginBottom: '8px' }}>Loading organization data...</div>
                        <div style={{ color: '#666' }}>Please wait while we load your organization and brands.</div>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb
                style={{ margin: '16px 0' }}
                items={[
                    { title: user.organisation?.name || 'Organization' },
                    { title: 'Analytics' },
                    { title: 'Data Selection' }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                {/* Header */}
                <div style={{ marginBottom: '24px' }}>
                    <Title level={2} style={{ margin: 0, marginBottom: '8px' }}>
                        Data Selection
                    </Title>
                    <Text style={{ color: '#6b7280', fontSize: '16px' }}>
                        Select the data you want to analyze. Your selections will be used for the Visualization and Statement Sentiment views.
                    </Text>
                </div>

                {/* AI Quota Status */}
                {quotaStatus?.enforcementEnabled && (
                    <div style={{ marginBottom: '24px' }}>
                        <AIQuotaWidget compact />
                    </div>
                )}

                {/* Filters Section */}
                <Card
                    style={{
                        borderRadius: '16px',
                        marginBottom: '24px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    <div style={{
                        marginBottom: '20px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            color: '#1f2937',
                            fontSize: '18px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <FilterOutlined style={{ color: '#2563eb' }} />
                            Analysis Filters
                        </h3>
                    </div>
                    <BrandEquityFilters
                        value={filters}
                        onChange={handleFiltersChange}
                        cohorts={cohortsData || []}
                        brands={brandsData || []}
                        surveys={surveysData || []}
                        onBrandChange={handleBrandChange}
                    />
                    <Divider />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CalculatorOutlined />}
                            onClick={handleCalculate}
                            loading={calculating}
                            disabled={!filters.brandId || (!filters.period && (!filters.dateFrom || !filters.dateTo))}
                            style={{
                                borderRadius: '8px',
                                minWidth: '200px',
                                height: '48px',
                                fontSize: '16px'
                            }}
                        >
                            Calculate & View Results
                        </Button>
                    </div>
                </Card>
            </div>

            {/* Subscription Upgrade Modal */}
            <SubscriptionUpgradeModal
                visible={showUpgradeModal}
                onCancel={() => setShowUpgradeModal(false)}
                onUpgrade={(plan) => {
                    // Redirect to billing page
                    window.location.href = '/dashboard/organisation?tab=billing';
                }}
                currentPlan={brandEquityUpgradeInfo.currentPlan}
                requiredPlan={brandEquityUpgradeInfo.requiredPlan}
                featureName="Brand Equity Calculations"
                brandName={brandsData?.find(b => b.id === filters.brandId)?.name}
            />
        </Content>
    );
};

export default DataSelectionPage;
